import { createSelector } from '@reduxjs/toolkit';
import type { GetV2ProviderBillingEncountersApiArg } from '@store/generated/billing';
import type { GetV2ProviderClaimsApiArg } from '@store/generated/claims';
import type { RootState } from '@store/types';

import type { Claim, ClaimAnnotation } from '../types';

import { billingApi, billingEncountersApi } from './api';
import type { ClaimScrubberUIState } from './slice';

interface ClaimFilters {
  page?: number;
  pageSize?: number;
  patientId?: string | null;
  status?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

interface EncounterFilters {
  page?: number;
  pageSize?: number;
  patientId?: string | null;
  encounterStatus?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

/**
 * Selector for claims list from RTK Query cache
 */
export const selectClaimsData = (filters?: ClaimFilters) => {
  // Build query params, excluding null/undefined values
  const queryParams: GetV2ProviderClaimsApiArg = {
    page: filters?.page ?? 1,
    pageSize: filters?.pageSize ?? 20,
    sortBy: 'created_at',
    sortOrder: 'desc',
  };

  // Only add optional params if they have values
  if (filters?.patientId) queryParams.patientId = filters.patientId;
  if (filters?.status) queryParams.status = filters.status;
  if (filters?.dateFrom) queryParams.dateFrom = filters.dateFrom;
  if (filters?.dateTo) queryParams.dateTo = filters.dateTo;

  return createSelector(
    billingApi.endpoints.getV2ProviderClaims.select(queryParams),
    result => result.data?.claims ?? [],
  );
};

/**
 * Selector for billing encounters from RTK Query cache
 */
export const selectBillingEncountersData = (filters?: EncounterFilters) => {
  // Build query params, excluding null/undefined values
  const queryParams: GetV2ProviderBillingEncountersApiArg = {
    page: filters?.page ?? 1,
    perPage: filters?.pageSize ?? 20,
    sortBy: 'encounter_date',
    sortOrder: 'desc',
  };

  // Only add optional params if they have values
  if (filters?.patientId) queryParams.patientId = filters.patientId;
  if (filters?.encounterStatus) queryParams.status = filters.encounterStatus;
  if (filters?.dateFrom) queryParams.dateFrom = filters.dateFrom;
  if (filters?.dateTo) queryParams.dateTo = filters.dateTo;

  return createSelector(
    billingEncountersApi.endpoints.getV2ProviderBillingEncounters.select(queryParams),
    result => result.data?.encounters ?? [],
  );
};

/**
 * Selector for a single claim by ID
 */
export const selectClaimById = (claimId: string) =>
  createSelector(
    billingApi.endpoints.getV2ProviderClaimsByClaimId.select({ claimId }),
    result => result.data ?? null,
  );

/**
 * Selector for claim statistics
 */
export const selectClaimStats = (filters?: ClaimFilters) =>
  createSelector(selectClaimsData(filters), (claims: Claim[]) => ({
    total: claims.length,
    draft: claims.filter(c => c.status === 'draft').length,
    submitted: claims.filter(c => ['submitted', 'acknowledged', 'accepted', 'rejected'].includes(c.status ?? '')).length,
    processing: claims.filter(c => c.status === 'processing').length,
    scrubbing: claims.filter(c => c.status === 'scrubbing').length,
  }));

/**
 * Selector for unbilled encounters count
 */
export const selectUnbilledEncountersCount = (patientId?: string) => {
  const filters: EncounterFilters = { encounterStatus: 'pending' };
  if (patientId) filters.patientId = patientId;

  return createSelector(selectBillingEncountersData(filters), encounters => encounters.length);
};

/**
 * Selector for total charges across claims
 */
export const selectTotalCharges = (filters?: ClaimFilters) =>
  createSelector(
    selectClaimsData(filters),
    (claims: Claim[]) =>
      claims.reduce((total, claim) => total + (claim.total_charge_cents || 0), 0) / 100, // Convert cents to dollars
  );

/**
 * Selector for claims grouped by status
 */
export const selectClaimsByStatus = (filters?: ClaimFilters) =>
  createSelector(selectClaimsData(filters), (claims: Claim[]) => {
    const grouped: Record<string, Claim[]> = {
      draft: [],
      finalized: [],
      processing: [],
      scrubbing: [],
    };

    claims.forEach(claim => {
      const status = claim.status || 'draft';
      if (grouped[status]) {
        grouped[status].push(claim);
      }
    });

    return grouped;
  });

/**
 * Selector for claims requiring action
 */
export const selectClaimsRequiringAction = (filters?: ClaimFilters) =>
  createSelector(selectClaimsData(filters), (claims: Claim[]) =>
    claims.filter(claim => claim.status === 'draft' || claim.status === 'scrubbing'),
  );

// ============================================================================
// Claim Scrubber UI Selectors
// ============================================================================

/**
 * Base selector for claim scrubber UI state
 */
const selectClaimScrubberUI = (state: RootState): ClaimScrubberUIState | undefined =>
  state.claimScrubber;

/**
 * Selector for panel state
 */
export const selectScrubberPanel = createSelector(
  [selectClaimScrubberUI],
  (scrubberUI) => scrubberUI?.panel || {
    isOpen: false,
    position: 'bottom' as const,
    isCollapsed: false,
    width: 400, // Default width for sidebar
  }
);

/**
 * Selector for navigation state
 */
export const selectScrubberNavigation = createSelector(
  [selectClaimScrubberUI],
  (scrubberUI) => scrubberUI?.navigation || {
    highlightedField: null,
    highlightedBox: null,
  }
);

/**
 * Selector for user preferences
 */
export const selectScrubberPreferences = createSelector(
  [selectClaimScrubberUI],
  (scrubberUI) => scrubberUI?.preferences || {
    autoOpenOnErrors: true,
    persistPosition: true,
  }
);

/**
 * Combined selector for all scrubber UI state
 */
export const selectScrubberUIState = createSelector(
  [selectScrubberPanel, selectScrubberNavigation, selectScrubberPreferences],
  (panel, navigation, preferences) => ({
    panel,
    navigation,
    preferences,
  })
);

/**
 * Selector to check if annotations should be shown
 * Returns true if claim has annotations
 */
export const selectShouldShowAnnotations = (claim: Claim | null | undefined) => {
  // If there are annotations, the claim has been scrubbed
  return (claim?.annotations?.length ?? 0) > 0;
};

/**
 * Selector for annotation counts from a claim
 */
export const selectAnnotationCounts = (claim: Claim | null | undefined) => {
  const annotations: ClaimAnnotation[] = claim?.annotations ?? [];
  return {
    errors: annotations.filter((a) => a?.severity === 'error').length,
    warnings: annotations.filter((a) => a?.severity === 'warning').length,
    info: annotations.filter((a) => a?.severity === 'info').length,
    total: annotations.length,
    shouldShow: selectShouldShowAnnotations(claim),
  };
};

/**
 * Selector for claim annotations
 * Returns annotations if they exist
 */
export const selectClaimAnnotations = (claim: Claim | null | undefined) => {
  return claim?.annotations ?? [];
};
